import { useState, useEffect } from 'react'

// Tauri 环境下的 window 扩展
declare global {
  interface Window {
    isTauriApp?: () => boolean
    tauriCheckFileExists?: (path: string) => Promise<boolean>
    tauriOpenFile?: (path: string) => Promise<boolean>
  }
}

interface FileViewerDialogProps {
  fileUrl: string
  fileName?: string
  fileType?: string
  localFilePath?: string | null // 本地文件路径（Tauri环境）
  onClose: () => void
  onPrev?: () => void
  onNext?: () => void
  hasPrev?: boolean
  hasNext?: boolean
  currentIndex?: number
  totalCount?: number
  allowDownload?: boolean
  initialRotation?: number // 初始旋转角度（0, 90, 180, 270）
  onRotationChange?: (rotation: number) => void // 旋转变化回调
  // 分享链接相关
  shareLink?: string // 当前文件的分享链接
  onShareLinkChange?: (link: string) => void | Promise<void> // 分享链接变化回调
  onOpenLinkSelector?: () => void // 打开链接选择对话框的回调
  showShareLinkInput?: boolean // 是否显示分享链接输入框（编辑模式）
  shareLinkNotLoggedInMessage?: string // 未登录时的提示信息
}

/**
 * 文件查看器组件
 * 支持：查看、下载、切换
 */
export const FileViewerDialog = ({
  fileUrl,
  fileName,
  fileType,
  localFilePath,
  onClose,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
  currentIndex,
  totalCount,
  allowDownload = true,
  initialRotation = 0,
  onRotationChange,
  shareLink = '',
  onShareLinkChange,
  onOpenLinkSelector,
  showShareLinkInput = false,
  shareLinkNotLoggedInMessage
}: FileViewerDialogProps) => {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fileContent, setFileContent] = useState<string>('')
  const [isTextFile, setIsTextFile] = useState(false)
  const [rotation, setRotation] = useState(initialRotation)
  const [localFileExists, setLocalFileExists] = useState<boolean | null>(null) // null: 未检查, true: 存在, false: 不存在
  const [showLinkPanel, setShowLinkPanel] = useState(false)
  const [linkInput, setLinkInput] = useState(shareLink)

  useEffect(() => {
    // 检测是否是文本文件
    const textTypes = ['text/plain', 'text/html', 'text/css', 'text/javascript', 'application/json']
    const isTxt = textTypes.includes(fileType || '') || 
                  (fileName && /\.(txt|md|json|html|css|js|ts|tsx|jsx|xml|yaml|yml)$/i.test(fileName))
    
    setIsTextFile(!!isTxt)

    if (isTxt) {
      // 加载文本文件内容
      setIsLoading(true)
      setError(null)
      fetch(fileUrl)
        .then(response => {
          if (!response.ok) throw new Error('文件加载失败')
          return response.text()
        })
        .then(text => {
          setFileContent(text)
          setIsLoading(false)
        })
        .catch(err => {
          setError(err.message)
          setIsLoading(false)
        })
    } else {
      setIsLoading(false)
    }
  }, [fileUrl, fileType, fileName])

  // 重置链接状态
  useEffect(() => {
    setLinkInput(shareLink)
    setShowLinkPanel(false)
  }, [shareLink, fileUrl])

  // 检查本地文件是否存在（仅在 Tauri 环境且有 localFilePath 时）
  useEffect(() => {
    if (localFilePath && typeof window.isTauriApp === 'function' && window.isTauriApp()) {
      console.log('[FileViewerDialog] 开始检查本地文件:', localFilePath)
      // 检查文件是否存在
      if (typeof window.tauriCheckFileExists === 'function') {
        setLocalFileExists(null) // 重置状态（显示加载中）
        window.tauriCheckFileExists(localFilePath)
          .then(exists => {
            setLocalFileExists(exists)
            console.log('[FileViewerDialog] 本地文件检查结果:', localFilePath, exists)
            if (!exists) {
              console.warn('[FileViewerDialog] 文件不存在:', localFilePath)
            }
          })
          .catch(err => {
            console.error('[FileViewerDialog] 检查本地文件失败:', err, '路径:', localFilePath)
            setLocalFileExists(false)
          })
      } else {
        console.warn('[FileViewerDialog] tauriCheckFileExists 函数不可用')
        setLocalFileExists(false)
      }
    } else {
      if (!localFilePath) {
        console.log('[FileViewerDialog] 没有本地文件路径')
      } else {
        console.log('[FileViewerDialog] 不在 Tauri 环境或 isTauriApp 不可用')
      }
      setLocalFileExists(false)
    }
  }, [localFilePath])

  // 键盘快捷键
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowLeft' && hasPrev && onPrev) {
        e.preventDefault()
        onPrev()
      } else if (e.key === 'ArrowRight' && hasNext && onNext) {
        e.preventDefault()
        onNext()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose, onPrev, onNext, hasPrev, hasNext])

  // 旋转功能（保留但暂未使用，可能在将来启用）
  void rotation
  void setRotation
  void onRotationChange

  const handleDownload = async () => {
    try {
      // 对于文本文件，直接下载内容
      if (isTextFile && fileContent) {
        const blob = new Blob([fileContent], { type: 'text/plain;charset=utf-8' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = fileName || 'file.txt'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      } else {
        // 对于其他文件，使用 fetch 下载并使用文件名
        const response = await fetch(fileUrl)
        if (!response.ok) throw new Error('下载文件失败')
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = fileName || 'file'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error('下载文件失败:', error)
      // 如果 fetch 失败，尝试直接打开链接
      window.open(fileUrl, '_blank')
    }
  }

  // 保存分享链接
  const handleSaveShareLink = async () => {
    if (onShareLinkChange) {
      try {
        await onShareLinkChange(linkInput.trim())
        setShowLinkPanel(false)
      } catch (error) {
        // 如果保存失败，不关闭面板，让用户重试
        console.error('保存链接失败:', error)
      }
    }
  }

  // 清除分享链接
  const handleClearShareLink = () => {
    setLinkInput('')
    if (onShareLinkChange) {
      onShareLinkChange('')
    }
    setShowLinkPanel(false)
  }

  const handleOpenLocalFile = async () => {
    if (!localFilePath) return
    
    console.log('[FileViewerDialog] 尝试打开本地文件:', localFilePath)
    
    try {
      // 先检查文件是否存在
      if (typeof window.tauriCheckFileExists === 'function') {
        const exists = await window.tauriCheckFileExists(localFilePath)
        console.log('[FileViewerDialog] 文件存在检查结果:', exists)
        
        if (!exists) {
          const errorMsg = `源文件不存在，可能已被移动或删除\n路径: ${localFilePath}`
          console.warn('[FileViewerDialog]', errorMsg)
          alert(errorMsg)
          // 更新状态，隐藏按钮
          setLocalFileExists(false)
          return
        }
      }
      
      // 打开文件
      if (typeof window.tauriOpenFile === 'function') {
        const success = await window.tauriOpenFile(localFilePath)
        if (success) {
          console.log('[FileViewerDialog] 成功打开本地文件:', localFilePath)
        } else {
          const errorMsg = `无法打开文件，请检查文件是否存在\n路径: ${localFilePath}`
          console.error('[FileViewerDialog]', errorMsg)
          alert(errorMsg)
        }
      } else {
        const errorMsg = '当前环境不支持打开本地文件\n路径: ' + localFilePath
        console.warn('[FileViewerDialog]', errorMsg)
        alert(errorMsg)
      }
    } catch (error) {
      const errorMsg = `打开文件时出错: ${error instanceof Error ? error.message : String(error)}\n路径: ${localFilePath}`
      console.error('[FileViewerDialog]', errorMsg, error)
      alert(errorMsg)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center"
      onClick={onClose}
    >
      {/* 切换按钮 - 左侧 */}
      {hasPrev && onPrev && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onPrev()
          }}
          onTouchEnd={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onPrev()
          }}
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-white bg-opacity-80 hover:bg-opacity-100 active:bg-opacity-100 shadow-lg text-gray-700 hover:text-gray-900 transition-all touch-manipulation"
          title="上一个 (←)"
        >
          <svg width="20" height="20" className="sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
      )}

      {/* 切换按钮 - 右侧 */}
      {hasNext && onNext && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onNext()
          }}
          onTouchEnd={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onNext()
          }}
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-white bg-opacity-80 hover:bg-opacity-100 active:bg-opacity-100 shadow-lg text-gray-700 hover:text-gray-900 transition-all touch-manipulation"
          title="下一个 (→)"
        >
          <svg width="20" height="20" className="sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      )}

      <div
        className="bg-white rounded-lg shadow-2xl w-[95vw] sm:w-[90vw] max-w-4xl h-[90vh] sm:h-[85vh] max-h-[800px] flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
      >

        {/* 头部 */}
        <div className="flex-shrink-0 px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
          <div className="min-w-0 flex-1">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 truncate">查看文件</h3>
            {fileName && (
              <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate">{fileName}</p>
            )}
            {currentIndex !== undefined && totalCount !== undefined && (
              <p className="text-[10px] sm:text-xs text-gray-400 mt-1">
                {currentIndex + 1} / {totalCount}
              </p>
            )}
          </div>
          <div className="flex gap-1.5 sm:gap-2 flex-wrap">
            {/* 打开源文件按钮（有本地路径时显示，点击时会再次检查） */}
            {localFilePath && typeof (window as any).isTauriApp === 'function' && (window as any).isTauriApp() && (
              <button
                onClick={handleOpenLocalFile}
                className={`px-2 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm rounded-md transition-colors flex items-center gap-1 sm:gap-2 touch-manipulation ${
                  localFileExists === true 
                    ? 'bg-green-600 text-white hover:bg-green-700 active:bg-green-700' 
                    : localFileExists === false
                    ? 'bg-yellow-600 text-white hover:bg-yellow-700 active:bg-yellow-700'
                    : 'bg-gray-400 text-white hover:bg-gray-500 active:bg-gray-500'
                }`}
                title={`使用系统默认应用打开源文件${localFilePath ? '\n路径: ' + localFilePath : ''}`}
              >
                <svg width="14" height="14" className="sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
                <span className="hidden sm:inline">{localFileExists === null ? '检查中...' : localFileExists === true ? '打开源文件' : '打开源文件（可能不存在）'}</span>
              </button>
            )}
            {allowDownload && (
              <button
                onClick={handleDownload}
                className="px-2 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-700 rounded-md transition-colors flex items-center gap-1 sm:gap-2 touch-manipulation"
                title="下载文件"
              >
                <svg width="14" height="14" className="sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                <span className="hidden sm:inline">下载</span>
              </button>
            )}
            {showShareLinkInput && (
              <button
                onClick={() => {
                  // 如果提供了链接选择器回调，优先使用它
                  if (onOpenLinkSelector) {
                    onOpenLinkSelector()
                    return
                  }
                  // 否则使用原来的输入框方式
                  if (!onShareLinkChange && shareLinkNotLoggedInMessage) {
                    alert(shareLinkNotLoggedInMessage)
                    return
                  }
                  setShowLinkPanel(!showLinkPanel)
                }}
                className={`px-2 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm rounded-md transition-colors flex items-center gap-1 sm:gap-2 touch-manipulation ${
                  shareLink ? 'bg-cyan-600 text-white hover:bg-cyan-700 active:bg-cyan-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-200'
                }`}
                title="设置跳转链接"
              >
                <svg width="14" height="14" className="sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                </svg>
                <span className="hidden sm:inline">链接</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="px-2 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-200 rounded-md transition-colors touch-manipulation"
              title="关闭 (ESC)"
            >
              关闭
            </button>
          </div>
        </div>

        {/* 分享链接输入面板 */}
        {showLinkPanel && showShareLinkInput && (
          <div className="px-3 sm:px-6 py-3 border-b border-gray-200 bg-gray-50">
            <div className="text-sm font-medium text-gray-700 mb-2">设置跳转链接</div>
            <p className="text-xs text-gray-500 mb-3">
              输入分享链接地址，当此画布被分享时，点击此文件将跳转到链接的画布
            </p>
            <input
              type="text"
              value={linkInput}
              onChange={(e) => setLinkInput(e.target.value)}
              placeholder="例如: /share/abc123 或完整URL"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 mb-3"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSaveShareLink()
                } else if (e.key === 'Escape') {
                  setShowLinkPanel(false)
                }
              }}
            />
            <div className="flex gap-2 items-center">
              {/* 清除链接放最左边 */}
              {shareLink && (
                <button
                  onClick={handleClearShareLink}
                  className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                >
                  清除链接
                </button>
              )}
              {/* 已设置链接时显示打开链接 */}
              {shareLink && (
                <a
                  href={shareLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 text-sm text-cyan-600 hover:text-cyan-700 hover:underline transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  打开链接 ↗
                </a>
              )}
              <div className="flex-1" />
              <button
                onClick={() => setShowLinkPanel(false)}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSaveShareLink}
                className="px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded transition-colors"
              >
                保存
              </button>
            </div>
          </div>
        )}

        {/* 内容区域 */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-6 min-h-0" style={{
          transform: `rotate(${rotation}deg)`,
          transition: 'transform 0.3s ease',
          transformOrigin: 'center center'
        }}>
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-sm sm:text-base text-gray-500">加载中...</div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-sm sm:text-base text-red-500">加载失败: {error}</div>
            </div>
          ) : isTextFile ? (
            <div className="whitespace-pre-wrap break-words text-gray-800 font-mono text-xs sm:text-sm">
              {fileContent || '(空文件)'}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 px-4">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="sm:w-16 sm:h-16 mb-3 sm:mb-4 text-gray-500">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
              </svg>
              {fileName && (
                <h4 className="mb-3 sm:mb-4 text-lg sm:text-xl font-bold text-gray-800 text-center break-all px-4">{fileName}</h4>
              )}
              <p className="mb-3 sm:mb-4 text-sm sm:text-base">此文件类型不支持预览</p>
              {localFilePath && (
                <p className="mb-2 text-[10px] sm:text-xs text-gray-400 text-center max-w-md break-all">
                  本地路径: {localFilePath}
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                {/* 打开源文件按钮（有本地路径时显示，点击时会再次检查） */}
                {localFilePath && typeof (window as any).isTauriApp === 'function' && (window as any).isTauriApp() && (
                  <button
                    onClick={handleOpenLocalFile}
                    className={`px-3 py-2 sm:px-4 sm:py-2 text-xs sm:text-sm rounded-md transition-colors flex items-center justify-center gap-2 touch-manipulation ${
                      localFileExists === true 
                        ? 'bg-green-600 text-white hover:bg-green-700 active:bg-green-700' 
                        : localFileExists === false
                        ? 'bg-yellow-600 text-white hover:bg-yellow-700 active:bg-yellow-700'
                        : 'bg-gray-400 text-white hover:bg-gray-500 active:bg-gray-500'
                    }`}
                  >
                    <svg width="14" height="14" className="sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                      <polyline points="15 3 21 3 21 9"></polyline>
                      <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                    {localFileExists === null ? '检查中...' : localFileExists === true ? '打开源文件' : '打开源文件（可能不存在）'}
                  </button>
                )}
                {allowDownload && (
                  <button
                    onClick={handleDownload}
                    className="px-3 py-2 sm:px-4 sm:py-2 text-xs sm:text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 active:bg-blue-700 transition-colors touch-manipulation"
                  >
                    下载文件
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 底部提示 */}
        <div className="flex-shrink-0 px-3 sm:px-6 py-2 sm:py-3 border-t border-gray-200 bg-gray-50">
          <p className="text-[10px] sm:text-xs text-gray-500 text-center">
            <span className="hidden sm:inline">{hasPrev || hasNext ? '← → 切换 | ' : ''}ESC 键关闭 | 点击外部区域关闭</span>
          </p>
        </div>
      </div>
    </div>
  )
}

export default FileViewerDialog

